/**
 * Plan Summary Card
 * 
 * Simple explanation of what the client purchased (their entitlement).
 * No legal language, no contract management actions.
 * 
 * PRINCIPLES:
 * - Human-readable plan descriptions
 * - Focus on what the client is entitled to receive
 * - Clear distinction: this is about QUANTITY, not speed
 * - Clean, non-technical presentation
 */

import { Calendar, Package, Gift } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

export type PlanType = 'monthly_target' | 'prepaid_package' | 'trial';

interface PlanSummaryCardProps {
  planType: PlanType;
  totalLeads: number;
  startDate: Date;
  endDate: Date;
  remainingLeads: number;
  isActive: boolean;
}

const planConfig: Record<PlanType, {
  label: string;
  description: string;
  icon: typeof Package;
  accentClass: string;
}> = {
  monthly_target: {
    label: "Monthly Plan",
    description: "leads per month",
    icon: Calendar,
    accentClass: "text-primary bg-primary/10 border-primary/20",
  },
  prepaid_package: {
    label: "Prepaid Package",
    description: "leads total",
    icon: Package,
    accentClass: "text-accent bg-accent/10 border-accent/20",
  },
  trial: {
    label: "Trial Plan",
    description: "leads included",
    icon: Gift,
    accentClass: "text-green-600 bg-green-50 border-green-200",
  },
};

export function PlanSummaryCard({
  planType,
  totalLeads,
  startDate,
  endDate,
  remainingLeads,
  isActive,
}: PlanSummaryCardProps) {
  const config = planConfig[planType];
  const PlanIcon = config.icon;
  
  const formatDateRange = () => {
    const start = format(startDate, "MMM d");
    const end = format(endDate, "MMM d, yyyy");
    return `${start} – ${end}`;
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="flex items-stretch">
          {/* Plan Icon Section */}
          <div className={cn(
            "flex items-center justify-center w-20 border-r",
            isActive ? config.accentClass : "bg-muted text-muted-foreground"
          )}>
            <PlanIcon className="w-8 h-8" />
          </div>
          
          {/* Plan Details */}
          <div className="flex-1 p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-foreground">{config.label}</h3>
                  {isActive ? (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                      Active
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs">
                      Inactive
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">
                  <span className="font-medium text-foreground">{totalLeads}</span> {config.description}
                </p>
              </div>
              
              <div className="text-right">
                <p className="text-2xl font-bold text-foreground">{remainingLeads}</p>
                <p className="text-xs text-muted-foreground">remaining</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{formatDateRange()}</span>
            </div>
            
            <p className="text-xs text-muted-foreground mt-2 italic">
              Your plan guarantees {totalLeads} leads. Delivery settings affect how quickly they arrive, not how many you receive.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
